import { IObjectModelEntry } from '~/interfaces/objectModelInterfaces';
import { IUser } from '~/interfaces/userInterfaces';
import { IVOptionSetLocal } from "~/interfaces/vSelectInterfaces";
import {
  ConditionalRuleBuiltInValueTypeName, ObjectModelEntryConditionalRuleBuiltInValueTypes
} from "~/models/conditionalRules/conditionalRuleBuiltInValueType";
import ObjectModelPermittable from "~/models/objectModelPermissions/objectModelPermittable";
import ObjectModelRelation from "~/models/objectModelRelations/objectModelRelation";
import ProcessFlowSubmissionMappedVariable from "~/models/processFlows/processFlowSubmissionMappedVariable";
import PropertyUpdate from "~/models/propertyUpdate";
import GloballySelected from "../../../formability/object_model_builder/store/modules/selected";
import { ObjectModelActionType } from "../../enums/objectModelActionType";
import { ObjectModelDefaultActionType } from "../../enums/objectModelDefaultActionType";
import Guid from "../../helpers/guid";
import { ObjectHelper } from "../../helpers/objectHelper";
import { Hash } from "../../interfaces/hashInterfaces";
import { ILabelable, ILoggable } from "../../interfaces/propertyInterfaces";
import IRepo from "../../interfaces/repo";
import Repo from "../../state/modules/repo";
import IConditionalRuleContainer from "../conditionalRules/conditionalRuleContainer";
import ConditionalRuleSetFactory from "../conditionalRules/conditionalRuleSetFactory";
import ObjectModel from "../objectModel";
import ObjectModelProperty from "../objectModelProperties/objectModelProperty";
import ObjectModelConditionalFields, { ObjectModelActionConditionalField } from "../objectModels/objectModelConditionalFields";
import ObjectModelConditionalRuleSet from "../objectModels/objectModelConditionalRuleSet";
import { RelationType } from "../relationType";
import RelativeTimePeriod from "../relativeTimePeriod";
import ObjectModelBuilderModel from "../tracked/objectModelBuilderModel";
import ActionConfirmation from "./actionConfirmation";
import { ObjectModelActionCapability } from "./objectModelActionCapability";
import ObjectModelActionCapabilityManager from "./objectModelActionCapabilityManager";
import ObjectModelActionDefinition from "./objectModelActionDefinition";
import ObjectModelActionFormViewer from "./objectModelActionFormViewer";
import {IShallowProcessFlow} from '~/interfaces/processFlowInterfaces'
import {DurationType} from "~/enums/durationType";
import {DefaultEmailStyle} from "~/enums/defaultEmailStyle";

export default class ObjectModelAction extends ObjectModelPermittable(ObjectModelBuilderModel) implements IConditionalRuleContainer, ILabelable, ILoggable {
  action_type: ObjectModelActionType;
  sort_order: number;
  label_title: string;
  subtitle: string;
  linked_form_id: string;
  linked_form_title: string;
  restrict_form_viewers: boolean;
  object_model_action_form_viewers: Hash<ObjectModelActionFormViewer> = {};
  has_availability_condition: boolean;
  action_available_conditional_rule_sets: Hash<ObjectModelConditionalRuleSet> = {};
  referenced_property_id: string;
  new_value_id: string;
  client_id: string;
  action_availability_start: RelativeTimePeriod;
  action_availability_end: RelativeTimePeriod;
  allows_bulk: boolean;
  include_confirmation: boolean;
  require_note: boolean;
  action_confirmation: ActionConfirmation;
  action_client_id: string;
  default_action_type: ObjectModelDefaultActionType;
  is_logged: boolean;
  property_updates:  { [key: string]: PropertyUpdate} = {};
  linked_document_id: string;
  linked_document_title: string;
  require_relationship: boolean;
  open_in_new_tab: boolean;
  linked_process_flow_id: string;
  linked_process_flow_title: string;
  process_flow: IShallowProcessFlow
  treat_as_bulk: boolean;
  process_flow_submission_mapped_variables: { [key: string]: ProcessFlowSubmissionMappedVariable } = {};
  linked_account_relationship_id: string;
  linked_account_relationship_title: string;
  email_style:  DefaultEmailStyle.Default

  static repo: IRepo<ObjectModelAction> = new Repo<ObjectModelAction>('objectModelActions', ObjectModelAction);

  constructor() {
    super();
    this.defineProperty('client_id', Guid.create());

    if (this.propertyUpdatesArray.length === 0) {
      this.appendPropertyUpdate()
    }
    if (!this.action_availability_start) {
      this.createEmbeddedObject.action_availability_start(this, {type: DurationType.never});
    }
    if (!this.action_availability_end) {
      this.createEmbeddedObject.action_availability_end(this, {type: DurationType.never});
    }


    for (const definition of ObjectModelConditionalFields.ACTION_FIELDS) {
      const conditionalFieldName = `${definition.fieldName}_conditional_rule_sets`;
      this.except.push(conditionalFieldName);
      this[conditionalFieldName] = {};
      this.createEmbeddedObject[conditionalFieldName] = (parent, json) => {
        return ConditionalRuleSetFactory.createForContainer(this, definition, {}, true, false);
      };
      if (definition.defaultConditionalValue instanceof Function) {
        this.except.push(definition.fieldName);
        this.createEmbeddedObject[definition.fieldName] = definition.defaultConditionalValue;
      }
    }
  }

  initialize(root: ObjectModel, parent: ObjectModel, actionType: ObjectModelActionType, json: object = {}): this {
    this.rootModel = root;
    this.parent = parent;
    this.defineProperty('action_type', actionType);
    ObjectModelActionCapabilityManager.addDefaultProperties(this);

    this.loadFromJson(json);
    return this;
  }
  get conditionalRulesBuiltInValueOptions(): IVOptionSetLocal{
    return {
      valueKey: 'client_id',
      labelKey: 'referenceNameOrDefault',
      heading: "About Record",
      options: ObjectModelEntryConditionalRuleBuiltInValueTypes.map(x=> {return {client_id: `${x}`, referenceNameOrDefault: ConditionalRuleBuiltInValueTypeName[x]}} )
    }
  }

  get EMBEDDED_MODELS(): { [key: string]: {type: any, relationType: RelationType } } {
    const models = {
      'object_model_action_form_viewers': { type: ObjectModelActionFormViewer, relationType: RelationType.dictionary },
      'action_confirmation': { type: ActionConfirmation, relationType: RelationType.singleObject },
      'action_availability_start': { type: RelativeTimePeriod, relationType: RelationType.singleObject },
      'action_availability_end': { type: RelativeTimePeriod, relationType: RelationType.singleObject },
      'property_updates': {type: PropertyUpdate, relationType: RelationType.dictionary},
      'process_flow_submission_mapped_variables': {type: ProcessFlowSubmissionMappedVariable, relationType: RelationType.dictionary},
    };
    for (const definition of ObjectModelConditionalFields.ACTION_FIELDS) {
      models[`${definition.fieldName}_conditional_rule_sets`] = {type: ObjectModelConditionalRuleSet, relationType: RelationType.dictionary};
      if (definition.defaultConditionalValue instanceof Function) {
        models[definition.fieldName] = {type: Object, relationType: RelationType.singleObject};
      }
    }
    return models;
  }

  createEmbeddedObject = {
    object_model_action_form_viewers: (parent, json) => new ObjectModelActionFormViewer(parent, json),
    action_availability_start: (parent, json): RelativeTimePeriod =>  new RelativeTimePeriod(parent, 'action_availability_start', json),
    action_availability_end: (parent, json): RelativeTimePeriod =>   new RelativeTimePeriod(parent, 'action_availability_end', json) ,
    action_confirmation: (parent, json) => new ActionConfirmation(parent, json),
    property_updates: (parent, json): PropertyUpdate =>  new PropertyUpdate(parent, 'property_updates', json),
    process_flow_submission_mapped_variables: (parent, json): ProcessFlowSubmissionMappedVariable => new ProcessFlowSubmissionMappedVariable(parent, json),
  };


  get propertyUpdatesArray(): PropertyUpdate[]{
    return ObjectHelper.toArray(this.property_updates)
  }

  get processFlowSubmissionMappedVariables(): ProcessFlowSubmissionMappedVariable[] {
    return ObjectHelper.toArray(this.process_flow_submission_mapped_variables);
  }

  appendPropertyUpdate(json = {}){
    this.createEmbeddedObject.property_updates(this, json )
  }

  static get rootName(): string {
    return 'object_model_actions';
  }

  get rootName(): string {
    return 'object_model_actions';
  }

  get defaultLabelTitle(): string {
    return this.label_title || `Untitled ${this.displayName}`;
  }

  get isSelected(): boolean {
    return GloballySelected.objectModelAction.is(this);
  }

  get icon(): string {
    const type = this.action_type || ObjectModelActionType.DefaultAction;
    return ObjectModelActionDefinition.getIcon(type);
  }

  static get displayName(): string {
    return 'ObjectModelAction';
  }

  get displayName(): string {
    return ObjectModelActionDefinition.getDisplayName(this.action_type);
  }

  can(duty: ObjectModelActionCapability): boolean {
    return ObjectModelActionCapabilityManager.can(this.action_type, this.default_action_type, duty);
  }

  is(actionType: ObjectModelActionType): boolean {
    return actionType === this.action_type;
  }

  addToRepo(): void {
    ObjectModelAction.repo.add(this);
  }

  select(): void {
    GloballySelected.objectModelAction.set(this);
  }

  get isLast(): boolean {
    return this.sort_order === (<ObjectModel>this.parent).nextSortOrderFor(this.rootName) - 1;
  }

  get isFirst(): boolean {
    return this.sort_order <= 0;
  }

  moveUp(): void {
    if (!this.isFirst) {
      this.sort_order--;
      this.parent.updateActionPositions(this, this.sort_order);
    }
  }

  moveDown(): void {
    if (!this.isLast) {
      this.sort_order++;
      this.parent.updateActionPositions(this, this.sort_order);
    }
  }

  get formViewersArray(): ObjectModelActionFormViewer[] {
    return ObjectHelper.toArray(this.object_model_action_form_viewers);
  }

  appendFormViewer(props): ObjectModelActionFormViewer {
    const viewer = new ObjectModelActionFormViewer(this, props);
    this.trackCreationDeletion(viewer.client_id, null, viewer.asJson(), viewer);
    return viewer;
  }

  getConditionalRuleSets(fieldName: string): Hash<ObjectModelConditionalRuleSet> {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  getSortedConditionalRuleSets(fieldName: string): ObjectModelConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return ObjectHelper.toArray(ruleSets).sort((x, y) => x.sort_order < y.sort_order ? -1 : 1);
  }

  createNewRuleSet(fieldName: string, definition: ObjectModelActionConditionalField): ObjectModelConditionalRuleSet {
    return new ObjectModelConditionalRuleSet(fieldName, definition);
  }

  getReferencedControl(id: string): ObjectModelProperty | ObjectModelRelation {
    return ObjectModelProperty.repo.find(id) || ObjectModelRelation.repo.find(id);
  }

  getReferencableControls(): (ObjectModelProperty | ObjectModelRelation)[] {
    const properties = this.rootModel.objectModelPropertiesArray;
    const accountRelations = this.rootModel.accountRelationsArray;
    return [...properties, ...accountRelations];
  }

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      case 'action_availability_start':
        return 'Action Availability Start';
      case 'action_availability_end':
        return 'Action Availability End';
      default:
        return propName
    }
  }

  isAvailableForEntry(entry: IObjectModelEntry, user: IUser, contentView): boolean {
    return entry.view_data?.available_action_client_ids[contentView.id]?.includes(this.client_id);
  }

  reconcileAllowsBulk({processFlow}: {processFlow?: Pick<IShallowProcessFlow, 'user_interactions_enabled'>}) {
    // force not being in bulk if PF chosen has user interactions
    if (processFlow && processFlow.user_interactions_enabled) {
      this.allows_bulk = false
    }
  }

  setLinkedProcessFlow(processFlow: this['process_flow']) {
    this.process_flow = processFlow
    this.reconcileAllowsBulk({ processFlow: this.process_flow })
    this.linked_process_flow_id = this.process_flow.id;
    this.linked_process_flow_title = this.process_flow.title;
  }
}